import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { motion } from 'framer-motion'
import { Heart, MoreVertical, EyeOff, Eye, Trash2, Wand2 } from 'lucide-react'
import CustomAvatar from '../CustomAvatar'
import { getToken } from '@/utils/request'
import { addLikeImage, deleteImage, disLikeImage, setImagePrivate, setImagePublic } from '@/services/ai_images'
import { notify } from '@/utils/crypto'
import { useModel } from '@/plugin-model/useModel'

// Shadcn UI 组件
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'

const AIImage = ({ image, setImages }) => {
  const { viewSet } = useModel('view')
  const router = useRouter()
  const [isLiked, setIsLiked] = useState(image?.is_liked_by_user)
  const [likesCount, setLikesCount] = useState(image?.total_likes)
  const [isHovered, setIsHovered] = useState(false)

  const handleLike = () => {
    if (getToken()) {
      if (isLiked) {
        disLikeImage(image?.id).then((res) => {
          if (res.code === 200) {
            setIsLiked(false)
            setLikesCount(likesCount - 1)
          } else {
            notify(res.messsage, 'info')
          }
        })
      } else {
        addLikeImage(image.id).then((res) => {
          if (res.code === 200) {
            setIsLiked(true)
            setLikesCount(likesCount + 1)
          } else {
            notify(res.messsage, 'info')
          }
        })
      }
    } else {
      viewSet('loginDialogOpen', true)
    }
  }

  const [isPrivate, setIsPrivate] = useState(image?.is_private)

  const handlePrivate = () => {
    if (getToken()) {
      if (isPrivate) {
        setImagePublic(image?.id, false).then((res) => {
          if (res.code === 200) {
            setIsPrivate(false)
          } else {
            notify(res.messsage, 'info')
          }
        })
      } else {
        setImagePrivate(image?.id, true).then((res) => {
          if (res.code === 200) {
            setIsPrivate(true)
          } else {
            notify(res.messsage, 'info')
          }
        })
      }
    } else {
      viewSet('loginDialogOpen', true)
    }
  }

  const path = router.query.keyword ? router.pathname : router.asPath

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="overflow-hidden"
    >
      <Card
        className="group relative h-auto w-full cursor-pointer overflow-hidden border bg-card/50 shadow-sm transition-all duration-300 hover:shadow-md dark:bg-card/30"
        onClick={() => image?.cdn_img_url && router.push(`${path}?image_id=${image?.id}`, undefined, { shallow: true })}
      >
        {image?.cdn_img_url && (
          <div className="absolute left-0 top-0 z-20 flex w-full items-center justify-between bg-gradient-to-b from-black/60 via-black/30 to-transparent p-3 opacity-0 transition-all duration-300 group-hover:opacity-100">
            <div className="flex items-center gap-2">
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  router.push(`/user/${image?.user_id}`)
                }}
                className="rounded-full ring-2 ring-white/30 transition-all duration-300 hover:ring-primary/70"
              >
                <CustomAvatar avatar={image?.author_avatar} width={32} height={32} />
              </div>
              <div
                className="max-w-[100px] truncate text-sm font-medium text-white lg:max-w-[200px]"
                onClick={(e) => {
                  e.stopPropagation()
                  router.push(`/user/${image?.user_id}`)
                }}
                role="presentation"
              >
                {image?.author_nickname}
              </div>
            </div>
            <motion.div whileTap={{ scale: 0.9 }} transition={{ type: 'spring', stiffness: 400, damping: 17 }}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-white/20 hover:text-white"
                onClick={(e) => {
                  e.stopPropagation()
                  handleLike()
                }}
              >
                <Heart className={`h-4 w-4 ${isLiked ? 'fill-rose-500 text-rose-500' : ''}`} />
                {likesCount > 0 && (
                  <Badge variant="secondary" className="absolute -right-1 -top-1 h-4 min-w-4 bg-primary px-[0.35rem] text-[10px] text-primary-foreground">
                    {likesCount}
                  </Badge>
                )}
              </Button>
            </motion.div>
          </div>
        )}

        <div className="overflow-hidden">
          <div className="relative w-full overflow-hidden">
            {image?.cdn_img_url ? (
              <div className="relative h-full w-full">
                <img src={image.cdn_img_url} alt={image.text_prompts || 'AI生成图片'} className={`transition-all duration-500 ${isHovered ? 'scale-105' : 'scale-100'}`} />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted">{/* <ImageIcon className="h-12 w-12 text-muted-foreground/40" /> */}</div>
            )}
          </div>
        </div>

        {image?.cdn_img_url && (
          <div className="absolute bottom-0 left-0 z-10 w-full bg-gradient-to-t from-black/80 via-black/50 to-transparent p-3 opacity-0 transition-all duration-300 group-hover:opacity-100">
            <div className="flex w-full items-center justify-between">
              <div className="line-clamp-2 max-w-[calc(100%-40px)] text-left text-xs text-white/90">{image?.text_prompts}</div>

              {router.asPath === '/my/profile' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-white hover:bg-white/20 hover:text-white" onClick={(e) => e.stopPropagation()}>
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePrivate()
                      }}
                    >
                      {isPrivate ? (
                        <>
                          <Eye className="mr-2 h-4 w-4" />
                          <span>显示图片</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="mr-2 h-4 w-4" />
                          <span>隐藏图片</span>
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteImage(image?.id).then((res) => {
                          if (res.code === 200) {
                            notify('删除成功')
                            setImages((prevImages) => prevImages.filter((item) => item.id !== image.id))
                          } else {
                            notify('删除失败', 'warning')
                          }
                        })
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>删除图片</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <motion.div className="mt-3 w-full" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="secondary"
                className="w-full bg-white/10 text-xs text-white hover:bg-white/20"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  const baseParams = `?prompt=${image?.generate_type === 'jinshu' ? image?.jinshu?.prompt : image?.text_prompts}&negative_prompts=${image?.negative_prompts ?? ''}`
                  navigator.clipboard.writeText(baseParams)
                  let url = ''
                  if (image?.generate_type === 'jinshu') {
                    url = `/creation/magic_quill${baseParams}`
                  } else if (image?.generate_type.includes('stable_diffusion_three')) {
                    url = `/creation/sd_three${baseParams}`
                  } else if (image?.generate_type.includes('midjourney')) {
                    url = `/creation/mj${baseParams}`
                  } else {
                    url = `/creation${baseParams}`
                  }
                  router.push(url)
                }}
              >
                <Wand2 className="mr-1 h-3.5 w-3.5" />
                生成同款
              </Button>
            </motion.div>
          </div>
        )}
      </Card>
    </motion.div>
  )
}

export default AIImage
